/**
 * Build the Results/MVP view from the final sim state (offline; online adds the server's rating change).
 */
import type { PlayerIndex } from '@hf/shared';
import type { PlayerSim, SimState } from '@hf/sim';
import type { MatchResultsView, MatchSetup, PlayerResultLine } from '../ui-model/types';

/** A stat as a whole number: a missing / non-finite counter (older state, a sim field not set) reads 0, never NaN. */
const n = (v: number | undefined): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : 0;

const line = (setup: MatchSetup, s: SimState, i: 0 | 1): PlayerResultLine => {
  const p: PlayerSim = s.players[i];
  return {
    name: setup.slots[i].name,
    character: setup.slots[i].character,
    score: n(s.rules.score[i]),
    yards: n(p.statYards),
    passYards: n(p.statPassYards),
    tackles: n(p.statTackles),
    catches: n(p.statCatches),
    interceptions: n(p.statInterceptions),
    sacks: n(p.statSacks),
    baits: n(p.statBaits),
    touchdowns: n(p.statTouchdowns),
  };
};

/** MVP: touchdowns, then baits + takeaways, then yards; ties go to the winner. */
const mvpScore = (l: PlayerResultLine): number =>
  l.touchdowns * 1000 + (l.baits + l.interceptions + l.sacks) * 100 + l.yards + l.passYards;

export const resultsFromState = (setup: MatchSetup, s: SimState): MatchResultsView => {
  const lines: [PlayerResultLine, PlayerResultLine] = [line(setup, s, 0), line(setup, s, 1)];
  const winner = (s.rules.winner === 0 || s.rules.winner === 1 ? s.rules.winner : -1) as PlayerIndex | -1;
  const a = mvpScore(lines[0]);
  const b = mvpScore(lines[1]);
  const mvp: PlayerIndex = a === b ? (winner === 1 ? 1 : 0) : a > b ? 0 : 1;
  return { winner, lines, mvp };
};
